// P17 Slice A — the client identification-status derivation, as ONE shared pure
// source. Collapses the former duplicate: the server derivation in
// server/src/routes/cdd.js and the client CddClientSection.tsx inline twin (the
// F1/F2 "two copies silently drift" lesson from the P17.0a census). Both call
// sites now RUN this one function — nothing re-implements it.
//
// RULING (D1=(c), D3, 2026-07-19): identity-completeness is GENERIC CORE KYC
// STATUS — a read-side derivation core renders — NOT a compliance finding. So it
// STAYS CORE and is deliberately NOT routed through the compliance hook (no
// module, no screen, no activation). This slice is a de-duplication, not a hook.
//
// WHAT IT DERIVES: a verifier-stamped 'complete' (the stored status) always wins.
// A company carries no natural-person identity fields, so it passes its stored
// status through unchanged. Otherwise a natural person is 'complete' when name +
// nationality + a usable ID are all present, 'in-progress' on any partial, else
// the stored status.
//
// CONTRACT — the caller passes the ALREADY-RESOLVED stored status STRING (not the
// state object), because the two call sites default it DIFFERENTLY and byte-
// identity requires preserving EACH:
//   • server  (routes/cdd.js):      deriveIdentityStatus(cl,     st?.identityStatus || 'required')
//   • client  (CddClientSection):   deriveIdentityStatus(client, state.identityStatus)
// The server floors a missing status to 'required'; the client passes it through.
// This function does NOT default — it returns `stored` in every pass-through case,
// so each caller's own defaulting is reproduced exactly (recordings/cdd-identity
// walks the divergence rows where the two sites legitimately differ).
//
// THE hasNatReg FOLD — the old client's in-progress test carried an extra term:
//     client:  hasName || hasNationality || hasUsableId || hasNatReg
//     server:  hasName || hasNationality || hasUsableId
// These are PROVABLY EQUAL, not merely equal-on-the-cast. hasUsableId is DEFINED
// as `hasNatReg || (idDocumentNumber && not-expired)`, so hasNatReg ⇒ hasUsableId;
// therefore `X || hasUsableId || hasNatReg` === `X || hasUsableId` for every input
// (hasNatReg true ⇒ hasUsableId already true; hasNatReg false ⇒ the term is false).
// FOLDED here — both call sites now compute the server form. The recording asserts
// this tautology across the whole cast (the "say which": folded because provable).
//
// PURE: no React, no DOM, no network — loads unchanged in the Vite frontend and in
// a plain `node` harness (the src/lib/coiMatch.mjs rationale). Server consumes it
// via require(esm) (Node 20.19+, sync — no top-level await here).

export function deriveIdentityStatus(client, identityStatus) {
  const stored = identityStatus;
  const isCompany = (client?.type || "").toLowerCase() === "company";
  if (isCompany || stored === "complete") return stored;
  const hasName = !!(client?.firstName && client?.lastName);
  const hasNationality = !!client?.nationality;
  const hasNatReg = !!client?.nationalRegister;
  const idExp = client?.idDocumentExpiresAt ? new Date(client.idDocumentExpiresAt) : null;
  const hasUsableId = hasNatReg || (!!client?.idDocumentNumber && idExp && idExp > new Date());
  if (hasName && hasNationality && hasUsableId) return "complete";
  if (hasName || hasNationality || hasUsableId) return "in-progress";
  return stored;
}
